import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Composer } from "@/components/chat/Composer";
import { LogoMark } from "@/components/brand/Logo";
import { QuickActions } from "@/components/home/QuickActions";
import { ExploreGrid } from "@/components/home/ExploreGrid";
import { useMe } from "@/hooks/queries";
import { useUploads } from "@/hooks/useUploads";
import { startStream } from "@/lib/chatStore";
import { useResearchMode } from "@/hooks/useResearchMode";

export default function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const [draft, setDraft] = useState(() => location.state?.draft || "");
  const inputRef = useRef(null);
  const uploads = useUploads({ initial: location.state?.attach || [] });
  const research = useResearchMode();
  const { data: me } = useMe();
  const outOfCredits = me && me.credits?.balance < 1;

  useEffect(() => {
    inputRef.current?.focus({ preventScroll: true });
  }, [location.state?.focusComposer]);

  const send = () => {
    if (research.on) {
      const question = draft;
      setDraft("");
      research.start({ question }).then((res) => !res && setDraft(question));
      return;
    }
    const key = startStream({ content: draft, kind: "message", attachments: uploads.attachable, mode: research.chatMode });
    setDraft("");
    uploads.clear();
    navigate(`/chat?pending=${key}`);
  };

  return (
    <div className="h-full overflow-y-auto" data-testid="home-view">
      <div className="mx-auto flex w-full max-w-[900px] flex-col px-4 pb-20 pt-[9vh] sm:px-6 md:pt-[12vh]">
        <div className="flex flex-col items-center text-center cs-rise">
          <LogoMark size={56} className="mb-7" />
          <h1 className="text-[2rem] font-semibold leading-tight tracking-tight text-white sm:text-4xl lg:text-[2.75rem]" data-testid="home-heading">
            What can I help you with?
          </h1>
          <p className="mt-3 max-w-lg text-[15px] text-zinc-400 sm:text-base" data-testid="home-subheading">
            Ask anything, search the web with sources, and work with your files.
          </p>
        </div>
        <div className="mx-auto mt-9 w-full max-w-[720px] cs-rise" style={{ animationDelay: "60ms" }}>
          <Composer
            ref={inputRef}
            variant="hero"
            value={draft}
            onChange={setDraft}
            onSubmit={send}
            disabled={outOfCredits || research.starting}
            notice={outOfCredits ? "You're out of credits. Buying credit packs will be available soon." : research.starting ? "Starting your search…" : null}
            sendKey={me?.settings?.send_key}
            testIdPrefix="home-composer"
            uploads={uploads}
            research={research}
            autoFocus
          />
          <QuickActions
            mode={research.mode}
            onMode={(m) => {
              research.setMode(m);
              inputRef.current?.focus();
            }}
          />
        </div>
        <ExploreGrid />
      </div>
    </div>
  );
}
